import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import * as srpClient from 'secure-remote-password/client';
import { firstValueFrom } from 'rxjs';
import { AuthUserModel } from '../models/auth-user.model';
import { RegisterPayloadModel } from '../models/register-payload.model';
import { ValidateInvitationPayloadModel } from '../models/validate-invitation-payload.model';
import { LoginInitPayloadModel } from '../models/login-init-payload.model';
import { LoginInitResponseModel } from '../models/login-init-response.model';
import { LoginVerifyPayloadModel } from '../models/login-verify-payload.model';
import { LoginVerifyResponseModel } from '../models/login-verify-response.model';

const ACCESS_TOKEN_KEY = 'srp_access_token';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);

  private readonly _user = signal<AuthUserModel | null>(null);
  private readonly _loading = signal(false);
  private readonly _error = signal<string | null>(null);

  readonly user = this._user.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly isAuthenticated = computed(() => this._user() !== null);

  constructor() {
    this.restoreSession();
  }

  // ── Register ────────────────────────────────────────────────────────────────
  /**
   * Derives the SRP verifier client-side from the password — the raw password
   * never travels the network.
   */
  async register(
    email: string,
    invitationToken: string,
    password: string,
  ): Promise<void> {
    this._loading.set(true);
    this._error.set(null);
    try {
      const salt = srpClient.generateSalt();
      const normalizedEmail = email.trim().toLowerCase();
      const privateKey = srpClient.derivePrivateKey(salt, normalizedEmail, password);
      const verifier = srpClient.deriveVerifier(privateKey);
      const payload: RegisterPayloadModel = {
        email: normalizedEmail,
        invitationToken,
        salt,
        verifier,
      };

      await firstValueFrom(
        this.http.post<void>('/api/auth/register', payload),
      );
    } catch (error) {
      const message =
        error instanceof HttpErrorResponse
          ? (error.error?.message as string | undefined) ??
            'Registration failed. The invitation link may be invalid or expired.'
          : 'Registration failed. Please try again.';
      this._error.set(message);
      throw error;
    } finally {
      this._loading.set(false);
    }
  }

  async validateInvitation(
    email: string,
    invitationToken: string,
  ): Promise<void> {
    this._loading.set(true);
    this._error.set(null);

    try {
      const payload: ValidateInvitationPayloadModel = {
        email: email.trim().toLowerCase(),
        invitationToken,
      };

      await firstValueFrom(
        this.http.post<void>('/api/auth/register/validate', payload),
      );
    } catch (error) {
      const message =
        error instanceof HttpErrorResponse
          ? (error.error?.message as string | undefined) ??
            'This invitation link is invalid or has expired.'
          : 'Unable to validate the invitation link. Please try again.';
      this._error.set(message);
      throw error;
    } finally {
      this._loading.set(false);
    }
  }

  // ── Login (two-step SRP-6a) ──────────────────────────────────────────────
  /**
   * Step 1: send clientPublicKey (A) → receive salt + serverPublicKey (B).
   * Step 2: prove knowledge of password via M1 → receive M2 + JWT.
   * Step 3: verify M2 so the client confirms the server also knows the password.
   */
  async login(email: string, password: string): Promise<void> {
    this._loading.set(true);
    this._error.set(null);
    try {
      const normalizedEmail = email.trim().toLowerCase();
      // ── Step 1 ──
      const clientEphemeral = srpClient.generateEphemeral();
      const initPayload: LoginInitPayloadModel = {
        email: normalizedEmail,
        clientPublicKey: clientEphemeral.public,
      };

      const { salt, serverPublicKey, sessionId } = await firstValueFrom(
        this.http.post<LoginInitResponseModel>('/api/auth/login/init', initPayload),
      );

      // ── Step 2 ──
      const privateKey = srpClient.derivePrivateKey(salt, normalizedEmail, password);
      const clientSession = srpClient.deriveSession(
        clientEphemeral.secret,
        serverPublicKey,
        salt,
        normalizedEmail,
        privateKey,
      );

      const verifyPayload: LoginVerifyPayloadModel = {
        sessionId,
        clientProof: clientSession.proof,
      };

      const { serverProof, accessToken } = await firstValueFrom(
        this.http.post<LoginVerifyResponseModel>('/api/auth/login/verify', verifyPayload),
      );

      // ── Step 3: verify the server actually knows the verifier ──
      srpClient.verifySession(
        clientEphemeral.public,
        clientSession,
        serverProof,
      );

      this.storeToken(accessToken);
      this._user.set(this.decodeUser(accessToken));
    } catch (error) {
      this._error.set('Login failed. Please check your credentials and try again.');
      throw error;
    } finally {
      this._loading.set(false);
    }
  }

  // ── Logout ──────────────────────────────────────────────────────────────────
  logout(): void {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    this._user.set(null);
  }

  /** Returns the stored JWT for use by the auth interceptor. */
  getToken(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  }

  hasPermission(permissionCode: string): boolean {
    return this._user()?.permissions?.includes(permissionCode) ?? false;
  }

  hasAnyPermission(permissionCodes: string[]): boolean {
    const userPermissions = this._user()?.permissions ?? [];
    return permissionCodes.some((permissionCode) => userPermissions.includes(permissionCode));
  }

  // ── Private helpers ─────────────────────────────────────────────────────────

  private storeToken(token: string): void {
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
  }

  private restoreSession(): void {
    const token = this.getToken();
    if (!token) return;
    try {
      const raw = this.decodeRaw(token);
      if (raw.exp && raw.exp * 1000 < Date.now()) {
        this.logout();
        return;
      }
      this._user.set({
        sub: raw.sub,
        email: raw.email,
        firstName: raw.firstName,
        lastName: raw.lastName,
        organizationId: raw.organizationId,
        roleCode: raw.roleCode,
        permissions: raw.permissions ?? [],
      });
    } catch {
      this.logout();
    }
  }

  private decodeRaw(
    token: string,
  ): AuthUserModel & { exp?: number; iat?: number } {
    return JSON.parse(atob(token.split('.')[1])) as AuthUserModel & {
      exp?: number;
      iat?: number;
    };
  }

  private decodeUser(token: string): AuthUserModel {
    const raw = this.decodeRaw(token);
    return {
      sub: raw.sub,
      email: raw.email,
      firstName: raw.firstName,
      lastName: raw.lastName,
      organizationId: raw.organizationId,
      roleCode: raw.roleCode,
      permissions: raw.permissions ?? [],
    };
  }
}

